import GenderSelect from './gender-select';
import SocialSignOn from './social-sign-on';
import EmailPassword from './email-password';
import Iovation from './iovation';
import UiTracking from '../tracking/ui-tracking';
import { DynamicConf } from '@parship/psg-interfaces';
import Turnstile from './turnstile';

export default class Form {
	private readonly form: HTMLFormElement;
	private readonly genderSelect: GenderSelect;
	private dynamicConf: DynamicConf;
	private socialSignOn: SocialSignOn;
	private emailPassword: EmailPassword;
	private uiTrackingEnabled: boolean;

	public constructor(container: HTMLFormElement) {
		this.form = container;
		this.dynamicConf = window.dynamicConf;
		this.uiTrackingEnabled = this.form.dataset.biEnabled === 'true';

		if (this.dynamicConf?.isIovationActive) {
			new Iovation(container, this.dynamicConf.brandName);
		}

		if (
			this.dynamicConf?.turnstile?.isEnabled &&
			this.form.dataset.cfTurnstile === 'true' &&
			!window.navigator.userAgent.startsWith('PEG')
		) {
			new Turnstile(this.form, this.dynamicConf.turnstile.siteKey);
		}

		this.form.addEventListener('submit', (event: Event) => this.onSubmit(event));
		this.emailPassword = new EmailPassword(this.form);

		if (!this.form.classList.contains('no-gender-select')) {
			this.genderSelect = new GenderSelect(
				container,
				this.form.classList.contains('preselect'),
				this.form.classList.contains('hetero'),
				() => this.onSexInput()
			);
		}

		this.setTerms();

		this.socialSignOn = new SocialSignOn(this.form, this.genderSelect, this.dynamicConf);
		this.updateA11y(false);

		if (
			this.form.classList.contains('show-complete-form') ||
			(this.form.classList.contains('mobile-show-complete-form') && window.innerWidth < 768)
		) {
			this.showCompleteForm();
		}

		if (this.form.classList.contains('mobile-hide-complete-form') && window.innerWidth >= 768) {
			this.genderSelect?.updateA11y(true);
		}

		window.addEventListener('message', (event: MessageEvent) => {
			if (event.origin === window.location.origin && event.data === 'reset-modal') {
				this.form.reset();
				this.emailPassword.hideAllErrors();
				this.genderSelect?.hideError();
			}
		});

		this.initUiTracking();
	}

	private initUiTracking(): void {
		if (!this.uiTrackingEnabled) {
			return;
		}

		this.form.querySelectorAll('.terms a').forEach((link: HTMLAnchorElement) => {
			link.addEventListener(
				'click',
				() => {
					UiTracking.trackUIEvent(
						'registration-form',
						'legal-link',
						'click',
						link.pathname
					);
				},
				{ once: true }
			);
		});
	}

	private onSexInput(): void {
		this.showCompleteForm();
		if (this.genderSelect.isValidSex()) {
			this.socialSignOn.setAppleButtonHref(this.genderSelect.getSelectedSex());
			this.socialSignOn.setGoogleButtonHref(this.genderSelect.getSelectedSex());
		}
	}

	private setTerms(): void {
		const terms: HTMLElement = this.form.querySelector('.terms');
		if (!this.genderSelect && this.dynamicConf?.registration?.legalTextTermsOfService) {
			terms.innerHTML = this.dynamicConf?.registration.legalTextTermsOfService;
		} else if (this.dynamicConf?.registration?.legaltext) {
			terms.innerHTML = this.dynamicConf?.registration.legaltext;
		}
	}

	private updateA11y(enabled: boolean): void {
		const hiddenElements: NodeListOf<HTMLElement> = this.form.querySelectorAll(
			'.hidden[aria-hidden],.mobile-hidden[aria-hidden]'
		);
		const links: NodeListOf<HTMLAnchorElement> = this.form.querySelectorAll(
			'.terms a, .apple-reg-button, .google-reg-button'
		);

		if (enabled) {
			hiddenElements.forEach((element: HTMLElement) =>
				element.removeAttribute('aria-hidden')
			);
			links.forEach((link: HTMLAnchorElement) => link.removeAttribute('tabindex'));
		} else {
			hiddenElements.forEach((element: HTMLElement) =>
				element.setAttribute('aria-hidden', 'true')
			);
			links.forEach((link: HTMLAnchorElement) => (link.tabIndex = -1));
		}

		this.emailPassword.updateA11y(enabled);
	}

	private validateBeforeSubmit(): boolean {
		let isValid: boolean = true;

		if (!this.emailPassword.validate()) {
			isValid = false;
		}
		if (this.genderSelect?.validate() === false) {
			isValid = false;
		}

		return isValid;
	}

	private showCompleteForm(): void {
		this.updateA11y(true);
		this.genderSelect?.updateA11y(true);

		this.form.classList.add('show-complete-form');
		document.body.classList.add('form-is-complete-visible');
	}

	private onSubmit(event: Event): void {
		if (!this.form.classList.contains('show-complete-form')) {
			this.showCompleteForm();
			event.preventDefault();
		} else if (!this.validateBeforeSubmit()) {
			event.preventDefault();
		} else if (this.uiTrackingEnabled) {
			UiTracking.trackUIEvent('registration-form', 'form', 'submit-success');
		}
	}
}
